import commonStore from "../Store/marco/commonStore";
import emissionViewStore from "../Store/marco/emissionViewStore";
import { categoryDtoMap } from "../EmissionView/services/categoryDtoMap";
import { categoryRouteMap } from "../EmissionView/services/categoryRouteMap";
import { getConstByStandard } from "../EmissionView/utils/getConstFileByStandard";

export const IMPORT_MODULES = Object.freeze({
  EA: "emission_activity",
  DE: "data_entry",
  EF: "emission_factor",
  OL: "organization_list",
});

const MODULE_META = Object.freeze({
  [IMPORT_MODULES.OL]: {
    abbr: "OL",
    label: "Organization List",
    color: "#0c6674",
    view: "",
  },
  [IMPORT_MODULES.EA]: {
    abbr: "EA",
    label: "Emission Activity",
    color: "#1a56a8",
    view: "Emission Activity",
  },
  [IMPORT_MODULES.DE]: {
    abbr: "DE",
    label: "Data Entry",
    color: "#534ab7",
    view: "Data Entry",
  },
  [IMPORT_MODULES.EF]: {
    abbr: "EF",
    label: "Emission Factor",
    color: "#993c1d",
    view: "Emission Factor",
  },
});

export const getModuleMeta = (module) =>
  MODULE_META[module] || {
    abbr: "",
    label: module || "Import",
    color: "#667085",
    view: "",
  };

export const resolveModuleFromImportType = (importType) => {
  if (["dataEntry", "dataEntrySummary", "dataEntrySplit"].includes(importType) || importType?.endsWith("_dataEntry")) {
    return IMPORT_MODULES.DE;
  }
  if (importType === "ef") return IMPORT_MODULES.EF;
  if (/^cat\d+_\d+$/.test(importType || "")) return IMPORT_MODULES.EA;
  return null;
};

export const isWorkflowImportType = (importType) =>
  Boolean(resolveModuleFromImportType(importType));

const findNavigationTarget = (importType) => {
  const categoryDropdown =
    getConstByStandard(emissionViewStore.standardId)?.categoryDropdown || [];

  for (const scope of categoryDropdown) {
    const tab = scope.category?.find(
      (item) => categoryRouteMap[item.label] === importType,
    );
    if (tab) {
      return {
        scope: scope.value || scope.label,
        tab: tab.label,
      };
    }
  }

  return { scope: "", tab: "" };
};

export const findTabByImportType = (importType) =>
  findNavigationTarget(importType).tab;

export const applyModuleNavigation = (job) => {
  const meta = getModuleMeta(job.module);
  if (meta.view) emissionViewStore._handleSetSelectedView(meta.view);

  if (job.module === IMPORT_MODULES.EA) {
    const target = findNavigationTarget(job.importType);
    if (target.scope && target.scope !== emissionViewStore.selectedScope) {
      emissionViewStore._handleSetSelectedScope(target.scope);
    }
    if (target.tab) emissionViewStore._handleSetSelectedTab(target.tab);
  }
};

export const createImportContext = (overrides = {}) => {
  const importType = overrides.importType || commonStore.excelImportType;
  const module =
    overrides.module || resolveModuleFromImportType(importType);
  const meta = getModuleMeta(module);
  const selectedTab =
    overrides.selectedTab ||
    (module === IMPORT_MODULES.EA
      ? findTabByImportType(importType)
      : emissionViewStore.selectedTab);

  return {
    module,
    moduleAbbr: overrides.moduleAbbr || meta.abbr,
    moduleLabel: meta.label,
    moduleColor: meta.color,
    importType,
    selectedTab,
    selectedYear: emissionViewStore.selectedYear,
    standardId: emissionViewStore.standardId,
    companyIds: emissionViewStore.authComArr,
    lang: commonStore.lang,
  };
};

const createRequestDTO = (context) => {
  const selectedTab = context.selectedTab || emissionViewStore.selectedTab;
  const routeCategory = categoryRouteMap[selectedTab] || "";
  const ghgCategory = categoryDtoMap[selectedTab] || "";
  const category = routeCategory.replace(/^cat/, "");

  if (context.module === IMPORT_MODULES.EA) {
    return {
      importType: context.importType,
      companyIds: context.companyIds,
      year: context.selectedYear,
      category: context.importType.replace(/^cat/, ""),
      ghgCategory,
      lang: context.lang,
      standardId: context.standardId,
    };
  }

  if (context.module === IMPORT_MODULES.DE) {
    return {
      importType: "dataEntry",
      companyIds: context.companyIds,
      year: context.selectedYear,
      category,
      ghgCategory,
      lang: context.lang,
      standardId: context.standardId,
    };
  }

  return {
    importType: "ef",
    ghgCategory,
    standardId: context.standardId,
  };
};

export const createImportFormData = (file, context) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("dto", JSON.stringify(createRequestDTO(context)));
  return formData;
};

export const exportModuleExcel = (context, isAll = false, isSummary = false) => {
  if (context.module === IMPORT_MODULES.EA) {
    return emissionViewStore.downloadEAExcel(commonStore.lang);
  }
  if (context.module === IMPORT_MODULES.DE) {
    return emissionViewStore.downloadDEExcel(
      commonStore.lang,
      commonStore.rootId,
      false,
      isSummary,
    );
  }
  return emissionViewStore.downloadEFExcel(commonStore.lang, isAll);
};

export const refreshModuleData = (module) => {
  if (!emissionViewStore.selectedYear) return null;

  if (module === IMPORT_MODULES.EA) {
    return emissionViewStore.getEAListData({ keepPage: true });
  }
  if (module === IMPORT_MODULES.DE) {
    return emissionViewStore.getDEListData({ keepPage: true });
  }
  if (module === IMPORT_MODULES.EF) {
    return emissionViewStore.getEFListData({ keepPage: true });
  }
  return null;
};
